import type { BoxProps } from '@chakra-ui/react'
import { HStack } from '@chakra-ui/react'
import { Box, Circle, Stack, Separator, Text } from '@chakra-ui/react'
import { url as gravatarUrl } from 'gravatar'
import type { FC } from 'react'
import { memo, Suspense, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Avatar } from '@app/components/ui/avatar'
import { Field } from '@app/components/ui/field'
import { Tooltip } from '@app/components/ui/tooltip'
import useGetObject from '@app/hooks/useGetObject'
import ToggleInput from '@app/next/forms/toggleInput'
import Can from '@app/shared/authorization/can'
import Comments from '@app/shared/comments/comments'
import { useStore } from '@app/store'
import type { DomainNode } from '@app/types'
import { useStrategyCommentThreadCommentsQuery } from '@graphql/queries'
import type { CommentThread } from '@graphql/types'

type SendToSlackInputProps = {
  commentThread: CommentThread
}

const SendToSlackInput: FC<SendToSlackInputProps> = ({ commentThread }) => {
  const updateObject = useStore.use.updateObject()
  const { sendToSlack } = commentThread

  const onChange = async (newValue: boolean) => {
    await updateObject({ commentThread: { id: commentThread.id, sendToSlack: newValue } })
  }

  return (
    <Field label={<Text fontSize="xs">Send to Slack</Text>} orientation="horizontal">
      <ToggleInput name="sendToSlack" defaultValue={sendToSlack} onChange={onChange} />
    </Field>
  )
}

interface Props extends BoxProps {
  commentThread?: CommentThread
  node?: DomainNode
  onClick?: () => void
  onDoubleClick?: () => void
}

const CommentThreadCommentsFetcher: FC<{ id: string }> = ({ id }) => {
  const { strategyId } = useParams()
  const [commentData, setCommentData] = useState(null)
  const [{ data }] = useStrategyCommentThreadCommentsQuery({
    variables: { strategyId, commentThreadId: id },
    pause: !!commentData
  })

  useEffect(() => {
    if (data) {
      setCommentData(data)
    }
  }, [data])

  return null
}

const CommentThreadCard: FC<Props> = ({ commentThread, node, ...rest }) => {
  const { commentsCount, creatorId, id } = commentThread
  const creator = useGetObject(creatorId, 'user')
  const { selected } = node
  const strategyId = node?.strategyId
  const strategy = useGetObject(strategyId, 'strategy')
  const slackChannelId = strategy?.slackChannelId

  return (
    <Box
      pos="relative"
      display="inline-block"
      maxW="lg"
      p={2}
      // use parent's border radius - parent's border width to get these
      // https://drafts.csswg.org/css-backgrounds-3/#corner-shaping
      border="1px solid #ccc"
      borderRadius="29px 29px 29px 0"
      bgColor="bg"
      {...rest}
    >
      <Stack gap={4} separator={<Separator />}>
        <HStack justifyContent="space-between">
          <Box pos="relative">
            <Tooltip aria-label={creator.name} showArrow content={creator.name}>
              <Avatar
                borderWidth={1}
                borderColor="white"
                name={creator.name}
                size="lg"
                src={gravatarUrl(creator.email, { s: '80', protocol: 'https', d: '404' })}
              />
            </Tooltip>
            {commentsCount > 0 && (
              <Circle pos="absolute" bottom={-1} left={-1} borderWidth={1} borderColor="border" bgColor="bg" size={4}>
                <Text color="fg.muted" fontSize="xs" fontWeight="semibold">
                  {commentsCount}
                </Text>
              </Circle>
            )}
          </Box>
          {slackChannelId && selected && (
            <Can I="update" a="strategy">
              <Box me={1}>
                <SendToSlackInput commentThread={commentThread} />
              </Box>
            </Can>
          )}
        </HStack>
        {selected && (
          <Box>
            <Suspense>
              <CommentThreadCommentsFetcher id={id} />
            </Suspense>
            <Comments
              commentableId={id}
              commentableType="CommentThread"
              p={0}
              className="nodgrag"
              display={selected ? 'block' : 'none'}
              py={2}
              zIndex={1000}
              minWidth={64}
            />
          </Box>
        )}
      </Stack>
    </Box>
  )
}

export default memo(CommentThreadCard)
